import { userRoles } from '../models/userRoles';
import { roles } from '../models/roles';
import { UserRole } from '../types/UserRole';
import { Role } from '../types/Role';
import { BaseRepository } from '../../../classes/BaseRepository';
import { convertToAppError, AppError, AsyncResult, err, ok } from '@shared/utils';
import { HTTP_STATUS_NAMES, DATA_API_MESSAGES } from '@shared/constants';
import { db } from '../../../db';
import { retryableQuery } from '../../../functions/retryableQuery';
import { eq, inArray } from 'drizzle-orm';

/**
 * @group UserRole
 * @summary UserRole repository
 * 
 * <blockquote>
 * Provides data access methods for user role management, including CRUD operations and user role-based permission checks.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const userRoleRepository = new UserRoleRepository();
 * const userRole = await userRoleRepository.getUserRoleById('123');
 * ```
 * 
 */
export class UserRoleRepository extends BaseRepository<UserRole> {
  protected table = userRoles;

  /**
   * Assigns a role to a user
   * 
   * @param userId - The user ID
   * @param roleId - The role ID
   * @returns AsyncResult containing the created user role or error
   */
  async assignRoleToUser(userId: string, roleId: string): AsyncResult<UserRole, AppError> {
    if (!userId || !roleId) {
      return err(convertToAppError(
        'User ID and role ID are required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    // Check if the user-role assignment already exists
    const existingAssignmentResult = await this.findByFields({
      user_id: userId,
      role_id: roleId
    });

    if (existingAssignmentResult.isErr) {
      return err(existingAssignmentResult.getFail());
    }

    const existingAssignments = existingAssignmentResult.getSuccess();

    if (existingAssignments.length > 0) {
      return ok(existingAssignments[0]); // Return existing assignment
    }

    // Create new user-role assignment
    const createResult = await this.create({
      user_id: userId,
      role_id: roleId
    });

    if (createResult.isErr) {
      return err(createResult.getFail());
    }

    return ok(createResult.getSuccess());
  }

  /**
   * Gets all roles for a user
   * 
   * @param userId - The user ID
   * @returns AsyncResult containing user roles or error
   */
  async getUserRoles(userId: string): AsyncResult<UserRole[], AppError> {
    if (!userId) {
      return err(convertToAppError(
        'User ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return await this.findByFields({
      user_id: userId
    });
  }

  /**
   * Gets user roles with role details for multiple users
   * 
   * @param userIds - Array of user IDs
   * @returns AsyncResult containing user roles with role details or error
   */
  async getUserRolesByUserIds(userIds: string[]): AsyncResult<Array<{ user_id: string; role: Role }>, AppError> {
    if (!userIds || userIds.length === 0) {
      return ok([]);
    }

    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select({
              user_id: userRoles.user_id,
              role: roles
            })
            .from(userRoles)
            .innerJoin(roles, eq(userRoles.role_id, roles.id))
            .where(inArray(userRoles.user_id, userIds))
        );

        return queryResult.match({
          ok: (userRolesWithRoles) => ok(userRolesWithRoles),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }
} 